// src/utils/dateUtils.js

/**
 * Format a timestamp to local date and time
 * @param {string|Date} timestamp - The timestamp to format
 * @param {boolean} includeTime - Whether to include time (default: false)
 * @param {boolean} includeSeconds - Whether to include seconds (default: false)
 * @returns {string} Formatted date/time string
 */
export const formatTimestamp = (timestamp, includeTime = false, includeSeconds = false) => {
    if (!timestamp) return 'N/A';

    const date = new Date(timestamp);

    // Check if date is valid
    if (isNaN(date.getTime())) return 'Invalid Date';

    const options = {
        year: 'numeric',
        month: 'short',
        day: '2-digit'
    };

    if (includeTime) {
        options.hour = '2-digit';
        options.minute = '2-digit';
        options.hour12 = true; // Use 12-hour format
    }

    if (includeSeconds && includeTime) {
        options.second = '2-digit';
    }

    return date.toLocaleString('en-US', options);
};

/**
 * Format a timestamp to show relative time (e.g., "2 hours ago")
 * @param {string|Date} timestamp - The timestamp to format
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (timestamp) => {
    if (!timestamp) return 'N/A';

    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSecs < 60) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;

    // For older dates, show the actual date
    return formatTimestamp(timestamp, false, false);
};

/**
 * Format a timestamp specifically for leave/attendance submissions
 * Shows both date and time with timezone awareness
 * @param {string|Date} timestamp - The timestamp to format
 * @returns {string} Formatted submission timestamp
 */
export const formatSubmissionTime = (timestamp) => {
    if (!timestamp) return 'N/A';

    const date = new Date(timestamp);

    // Check if date is valid
    if (isNaN(date.getTime())) return 'Invalid Date';

    return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
    });
};

/**
 * Get the user's local timezone
 * @returns {string} Timezone name (e.g., "Asia/Kolkata")
 */
export const getUserTimezone = () => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Format date for display (without time)
 * @param {string|Date} date - The date to format
 * @returns {string} Formatted date string
 */
export const formatDate = (date) => {
    if (!date) return 'N/A';

    const d = new Date(date);
    if (isNaN(d.getTime())) return 'Invalid Date';

    return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit'
    });
};

/**
 * Format date range
 * @param {string|Date} fromDate - Start date
 * @param {string|Date} toDate - End date
 * @returns {string} Formatted date range
 */
export const formatDateRange = (fromDate, toDate) => {
    if (!fromDate || !toDate) return 'N/A';

    const from = formatDate(fromDate);
    const to = formatDate(toDate);

    if (from === to) return from;
    return `${from} - ${to}`;
};
